import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";
import type { Banner, ServedAd } from "@/api/types";
import { SmartImage } from "@/components/ui";
import { AdVideo } from "@/features/ads/AdVideo";
import { SponsoredTag } from "@/features/ads/Sponsored";
import { trackClick, trackImpression } from "@/features/ads/api";
import { cn } from "@/lib/cn";
import { useReducedMotion } from "@/motion/useReducedMotion";

/** Desktop: fill the top block's height instead of the wide 1920×720 ratio. */
const FRAMED = "lg:aspect-auto lg:h-full lg:min-h-[400px]";

/** A CMS banner, or an ad slide (with its tracking token, and the ad itself for video slides). */
export type HeroSlide = Banner & { token?: string; ad?: ServedAd };

/**
 * Glowing hero banner carousel (auto-advances every 6s; pause control per WCAG 2.2.2).
 * `framed`: sits in the home page's three-column top block (category sidebar | hero | promo tiles),
 * so it drops its own page container and fills the column height on desktop.
 */
export function HeroCarousel({
  banners: all,
  framed = false,
  fill = false,
  label = "Featured",
}: {
  banners: HeroSlide[];
  framed?: boolean;
  /** Fill the parent box at every width (the home hero panel). */
  fill?: boolean;
  label?: string;
}) {
  const banners = all.slice(0, 5);
  const reduced = useReducedMotion();
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(!reduced);
  const count = banners.length;
  // Video slides stay up for their whole length (up to 30 s); image slides for 6 s.
  const current = banners[index];
  const dwell = current?.ad?.kind === "VIDEO" ? Math.min(Math.max((current.ad.duration_s ?? 6) * 1000, 6000), 30000) : 6000;
  useEffect(() => {
    if (!playing || count < 2) return;
    const t = window.setTimeout(() => setIndex((i) => (i + 1) % count), dwell);
    return () => window.clearTimeout(t);
  }, [playing, count, index, dwell]);
  const activeToken = banners[index]?.token;
  useEffect(() => {
    if (!activeToken) return;
    const t = window.setTimeout(() => trackImpression(activeToken), 1000);
    return () => window.clearTimeout(t);
  }, [activeToken]);
  if (!count) return null;
  const go = (i: number) => setIndex((i + count) % count);

  return (
    <section className={framed || fill ? "h-full min-w-0" : "container-rs pt-6"} aria-roledescription="carousel" aria-label={label}>
      <div
        className={cn(
          "dark:edge-light relative overflow-hidden rounded-[24px] border border-fg/[0.06] shadow-glow-3",
          framed && "lg:h-full",
          fill && "h-full rounded-[16px] shadow-none",
        )}
      >
        {banners.map((b, i) => {
          const internal = b.cta_url.startsWith("/");
          const cta = b.cta_label && (
            <span className="inline-flex h-11 items-center rounded-full bg-[var(--primary-strong)] px-6 text-sm font-semibold text-white shadow-glow-2">
              {b.cta_label}
            </span>
          );
          return (
            <div
              key={b.id}
              role="group"
              aria-roledescription="slide"
              aria-label={`${i + 1} of ${count}: ${b.title}`}
              aria-hidden={i !== index}
              className={cn(
                "absolute inset-0 transition-opacity duration-700",
                i === index ? "relative opacity-100" : "pointer-events-none opacity-0",
                framed && "lg:h-full",
                fill && "h-full",
              )}
            >
              {b.ad?.kind === "VIDEO" ? (
                <AdVideo
                  ad={b.ad}
                  variant="hero"
                  active={i === index}
                  boxClassName={cn(fill ? "h-full w-full" : "aspect-square w-full sm:aspect-[1920/720]", framed && !fill && FRAMED)}
                />
              ) : (
                <SmartImage
                  src={b.image}
                  mobileSrc={b.mobile_image}
                  alt=""
                  fallbackLabel={b.title}
                  width={1440}
                  maxWidth={1920}
                  sizes={framed ? "(min-width: 1280px) 720px, (min-width: 1024px) 60vw, 100vw" : "(min-width: 1280px) 1216px, 100vw"}
                  priority={i === 0}
                  className={cn(fill ? "h-full w-full" : "aspect-square w-full sm:aspect-[1920/720]", framed && !fill && FRAMED)}
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-r from-scrim/90 via-scrim/55 to-transparent" />
              <div
                className={cn(
                  "absolute inset-0 flex flex-col justify-end gap-3 p-5 pb-16 sm:justify-center sm:p-12 lg:max-w-[60%]",
                  framed && "lg:max-w-[75%] lg:p-10",
                  fill && "justify-end gap-2 p-5 pb-12 sm:justify-end sm:p-6 sm:pb-12 lg:max-w-full lg:p-6 lg:pb-12",
                )}
              >
                <h2
                  className={cn(
                    "text-[clamp(1.6rem,7vw,3rem)] font-bold leading-tight text-white sm:text-5xl",
                    framed && "lg:text-4xl",
                    fill && "text-xl sm:text-2xl lg:text-2xl",
                  )}
                  style={b.accent ? { textShadow: `0 0 32px ${b.accent}66` } : undefined}
                >
                  {b.title}
                </h2>
                {b.subtitle && <p className="max-w-lg text-sm text-white/85 sm:text-base">{b.subtitle}</p>}
                {cta &&
                  (internal ? (
                    <Link
                      to={b.cta_url}
                      tabIndex={i === index ? 0 : -1}
                      className="w-fit rounded-full"
                      onClick={() => b.token && trackClick(b.token)}
                    >
                      {cta}
                    </Link>
                  ) : (
                    <a
                      href={b.cta_url}
                      tabIndex={i === index ? 0 : -1}
                      className="w-fit rounded-full"
                      rel={b.token ? "noopener sponsored" : "noopener"}
                      onClick={() => b.token && trackClick(b.token)}
                    >
                      {cta}
                    </a>
                  ))}
              </div>
              {b.token && <SponsoredTag className="absolute left-4 top-4" />}
            </div>
          );
        })}
        {count > 1 && (
          <div className="absolute bottom-4 right-4 flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPlaying((p) => !p)}
              className="grid size-9 place-items-center rounded-full bg-void/70 text-ink backdrop-blur"
              aria-label={playing ? "Pause slideshow" : "Play slideshow"}
            >
              {playing ? <Pause className="size-4" aria-hidden /> : <Play className="size-4" aria-hidden />}
            </button>
            <button
              type="button"
              onClick={() => go(index - 1)}
              className="grid size-9 place-items-center rounded-full bg-void/70 text-ink backdrop-blur"
              aria-label="Previous slide"
            >
              <ChevronLeft className="size-4" aria-hidden />
            </button>
            <div className="flex">
              {banners.map((b, i) => (
                // 24×24 hit area (WCAG 2.5.8) around the small visible dot.
                <button
                  key={b.id}
                  type="button"
                  onClick={() => go(i)}
                  aria-label={`Show slide ${i + 1}`}
                  aria-current={i === index}
                  className="grid h-6 min-w-6 place-items-center rounded-full px-1"
                >
                  <span
                    className={cn("block h-1.5 rounded-full transition-all", i === index ? "w-6 bg-cyan" : "w-1.5 bg-fg/40")}
                    aria-hidden
                  />
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => go(index + 1)}
              className="grid size-9 place-items-center rounded-full bg-void/70 text-ink backdrop-blur"
              aria-label="Next slide"
            >
              <ChevronRight className="size-4" aria-hidden />
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
